// Claude (Anthropic) AI Provider
// Handles Claude 4.5 Sonnet with streaming, web search, and extended thinking

import { getOrCreateDeviceId, getSessionId } from '../utils'

// Global abort controller for canceling requests
let currentAbortController = null

export function stopClaude() {
  if (currentAbortController) {
    currentAbortController.abort()
    currentAbortController = null
  }
}

// Server-Sent Events streaming for Claude (bypasses 26s timeout)
// Also supports optional non-streaming mode with structured outputs
export async function callClaude(messages, userApiKey, model = 'claude-sonnet-4-20250514', options = {}) {
  currentAbortController = new AbortController()

  console.log('🔴 Calling Claude API')
  console.log('User API Key provided:', !!userApiKey)
  console.log('Model:', model)
  console.log('Options:', options)

  // If the user provided an API key, call Anthropic directly from the client
  if (userApiKey) {
    try {
      const result = await callClaudeDirect(messages, userApiKey, model, options, currentAbortController.signal)
      console.log('✅ Received response from Claude (direct client call)')
      return result
    } catch (error) {
      // Preserve AbortError semantics so the stop button works
      if (error.name === 'AbortError') {
        throw error
      }
      throw error
    }
  }

  // Fallback: use server-side API key via streaming endpoint
  console.log('📡 Using server-side Claude API (streaming)')
  
  // Get device ID for job persistence
  const deviceId = getOrCreateDeviceId()
  const sessionId = getSessionId()
  
  const response = await fetch('/api/ai-chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      messages,
      provider: 'anthropic',
      model,
      // Job persistence fields
      deviceId,
      sessionId
    }),
    signal: currentAbortController.signal
  })

  console.log('📡 Response status:', response.status)
  console.log('📡 Response headers:', response.headers.get('content-type'))

  if (!response.ok) {
    const errorText = await response.text()
    console.error('❌ Server error:', errorText)
    throw new Error('Claude API error: ' + errorText)
  }

  // Handle streaming response (Server-Sent Events)
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let fullContent = ''
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      
      // Process complete SSE messages
      const lines = buffer.split('\n')
      buffer = lines.pop() || '' // Keep incomplete line in buffer

      for (const line of lines) {
        if (!line.trim()) continue // Skip empty lines
        
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim()
          
          if (data === '[DONE]') {
            console.log('✅ Claude stream complete')
            break
          }

          try {
            const parsed = JSON.parse(data)
            console.log('📦 Claude event:', parsed.type)
            
            // Handle Claude API streaming events
            // See: https://docs.anthropic.com/en/api/messages-streaming
            if (parsed.type === 'content_block_delta') {
              // Text delta from Claude
              if (parsed.delta?.type === 'text_delta' && parsed.delta?.text) {
                const text = parsed.delta.text
                fullContent += text
                console.log('📝 Text chunk:', text.substring(0, 50))
                if (options.onChunk) {
                  options.onChunk(text)
                }
              }
            }
            else if (parsed.type === 'message_start') {
              console.log('🚀 Claude message started')
            }
            else if (parsed.type === 'message_delta') {
              console.log('📊 Message delta:', parsed.delta)
            }
            else if (parsed.type === 'message_stop') {
              console.log('✅ Claude message complete')
            }
            else if (parsed.type === 'error') {
              console.error('❌ Claude error:', parsed.error)
              throw new Error(parsed.error?.message || 'Claude API error')
            }
          } catch (e) {
            if (e.message.includes('error')) throw e
            // Log but don't fail on parse errors
            console.warn('⚠️ Failed to parse SSE line:', line)
          }
        }
        else if (line.startsWith('event: ')) {
          console.log('📡 Event type:', line.slice(7))
        }
      }
    }

    console.log('✅ Received full response from Claude:', fullContent.length, 'chars')
    
    return {
      content: fullContent,
      usage: {} // Usage info not available in streaming mode
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('🛑 Claude stream aborted')
      throw error
    }
    console.error('❌ Claude streaming error:', error)
    throw error
  }
}

// Direct Claude call using the Messages API from the browser when user API key is provided
async function callClaudeDirect(messages, apiKey, model, options = {}, signal) {
  const systemMessage = messages.find(m => m.role === 'system')
  const userMessages = messages.filter(m => m.role !== 'system')

  // Clean and validate messages - remove empty content
  const cleanedMessages = userMessages
    .filter(msg => msg.content && msg.content.trim()) // Remove empty messages
    .map(msg => ({
      role: msg.role,
      content: msg.content.trim()
    }))

  const isHaiku = model.includes('haiku')
  const maxTokens = isHaiku ? 1000 : 16000
  const extendedThinking = options.extendedThinking !== false
  const includeTools = !isHaiku && extendedThinking

  const requestBody = {
    model: model,
    system: systemMessage?.content || '',
    messages: cleanedMessages,
    max_tokens: maxTokens,
    temperature: isHaiku ? 0.3 : 1
  }

  // Add tools and extended thinking only when enabled
  if (includeTools) {
    requestBody.tools = [
      {
        type: 'web_search_20250305',
        name: 'web_search',
        max_uses: 3
      }
    ]
    requestBody.thinking = {
      type: 'enabled',
      budget_tokens: 5000
    }
  }

  const fetchOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01'
    },
    body: JSON.stringify(requestBody)
  }

  if (signal) {
    fetchOptions.signal = signal
  }

  const response = await fetch('https://api.anthropic.com/v1/messages', fetchOptions)

  if (!response.ok) {
    let errorPayload
    try {
      errorPayload = await response.json()
    } catch {
      throw new Error('Claude API error')
    }
    throw new Error(errorPayload.error?.message || 'Claude API error')
  }

  const data = await response.json()

  if (data.stop_reason === 'refusal') {
    throw new Error('The AI declined to respond to this request.')
  }

  let content = ''
  let citations = []

  if (Array.isArray(data.content)) {
    data.content.forEach(block => {
      if (block.type === 'text') {
        content += block.text
        if (block.citations && Array.isArray(block.citations)) {
          citations.push(...block.citations)
        }
      } else if (block.type === 'thinking') {
        content += '\n\n💭 **Claude used extended thinking for this response**\n'
      } else if (block.type === 'web_search_tool_result') {
        content += '\n\n🔍 [Web search performed]'
      }
    })
  }

  if (citations.length > 0) {
    content += '\n\n**Sources:**\n'
    citations.forEach((citation, i) => {
      if (citation.type === 'web_search_result_location') {
        content += `${i + 1}. [${citation.title}](${citation.url})\n`
      }
    })
  }

  return {
    content,
    usage: data.usage
  }
}

